import { BetaBadge } from '@shinetools/cashpilot'

import { AttachmentsCountBadge } from '@features/attachments/elements/AttachmentsCountBadge'
import { QuickStartCountBadge } from '@features/quickStart/elements/QuickStartCountBadge'
import { ToDoCountBadge } from '@views/thingsToDo/elements/ToDoCountBadge'

import { EmberRoute } from '../../../../../enums/emberRoute'
import { Scope } from '../../../../../enums/scope'
import { getOrganizationViewUrlPlaceholder } from '../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../utils/routing/reactRoute'
import { NavigationMenuId } from '../enums/navigationMenuId'
import { NavigationMenuItem } from '../types/NavigationMenuItem'

export const navigationMenuConfig: NavigationMenuItem<NavigationMenuId>[] = [
  {
    id: NavigationMenuId.QuickStart,
    iconLeft: 'squareCheck',
    label: 'navigation.menu.label.quick_start',
    to: getOrganizationViewUrlPlaceholder(reactRoute.organization.quickStart.getMainRoute()),
    badge: <QuickStartCountBadge />,
  },
  {
    id: NavigationMenuId.Overview,
    iconLeft: 'presentation',
    label: 'navigation.menu.label.overview',
    to: getOrganizationViewUrlPlaceholder(reactRoute.organization.dashboard.getMainRoute()),
  },
  {
    id: NavigationMenuId.ThingsToDo,
    iconLeft: 'listChecks',
    label: 'navigation.menu.label.things_to_do',
    to: getOrganizationViewUrlPlaceholder(reactRoute.organization.thingsToDo.getMainRoute()),
    badge: <ToDoCountBadge />,
  },
  {
    id: NavigationMenuId.Sales,
    iconLeft: 'fileText',
    label: 'navigation.menu.label.sales',
    subItems: [
      {
        id: NavigationMenuId.Invoices,
        label: 'navigation.menu.label.sales.invoices',
        to: EmberRoute.Invoices,
        scopes: Scope.InvoiceRead,
      },
      {
        id: NavigationMenuId.ImportedInvoices,
        label: 'navigation.menu.label.sales.imported_invoices',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.importedInvoices.getMainRoute()),
        scopes: Scope.InvoiceRead,
      },
      {
        id: NavigationMenuId.RecurringInvoices,
        label: 'navigation.menu.label.sales.recurring_invoices',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.recurringInvoices.getMainRoute()),
        scopes: Scope.RecurringInvoiceRead,
      },
      {
        id: NavigationMenuId.Estimates,
        label: 'navigation.menu.label.sales.estimates',
        to: EmberRoute.Quotes,
        scopes: Scope.OfferRead,
      },
      {
        id: NavigationMenuId.LegacyRecurringInvoices,
        label: 'navigation.menu.label.sales.recurring_invoices',
        to: EmberRoute.RecurringInvoices,
        scopes: Scope.RecurringInvoiceRead,
      },
      {
        id: NavigationMenuId.Products,
        label: 'navigation.menu.label.sales.products',
        to: EmberRoute.Products,
        scopes: Scope.ProductRead,
      },
      {
        id: NavigationMenuId.Clients,
        label: 'navigation.menu.label.sales.clients',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.clients.getMainRoute()),
        scopes: Scope.ContactRead,
      },
      {
        id: NavigationMenuId.Debtors,
        label: 'navigation.menu.label.sales.debtors',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.debtors.getMainRoute()),
        scopes: Scope.InvoiceRead,
      },
      {
        id: NavigationMenuId.DeliveryNotes,
        label: 'navigation.menu.label.sales.delivery_notes',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.deliveryNotes.getMainRoute()),
        scopes: Scope.InvoiceRead,
      },
    ],
  },
  {
    id: NavigationMenuId.Expenses,
    iconLeft: 'wallet',
    label: 'navigation.menu.label.expenses',
    subItems: [
      {
        id: NavigationMenuId.Receipts,
        label: 'navigation.menu.label.expenses.receipts',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.uploads.getMainRoute()),
        scopes: Scope.UploadsRead,
        badge: <AttachmentsCountBadge />,
      },
      {
        id: NavigationMenuId.Vouchers,
        label: 'navigation.menu.label.expenses.vouchers',
        to: EmberRoute.Vouchers,
      },
      {
        id: NavigationMenuId.Purchases,
        label: 'navigation.menu.label.expenses.purchases',
        scopes: Scope.BillRead,
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.bills.getMainRoute()),
      },
      {
        id: NavigationMenuId.Payroll,
        label: 'navigation.menu.label.expenses.payroll',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.salary.getMainRoute()),
        scopes: Scope.SalaryRead,
      },
      {
        id: NavigationMenuId.Suppliers,
        label: 'navigation.menu.label.expenses.suppliers',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.suppliers.getMainRoute()),
        scopes: Scope.ContactRead,
      },
      {
        id: NavigationMenuId.Creditors,
        label: 'navigation.menu.label.expenses.creditors',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.creditors.getMainRoute()),
        scopes: Scope.BillRead,
      },
    ],
  },
  {
    id: NavigationMenuId.BankingReact,
    iconLeft: 'bank',
    label: 'navigation.menu.label.banking',
    to: EmberRoute.BankReact,
    scopes: Scope.BankReconciliationRead,
  },
  {
    id: NavigationMenuId.Banking,
    iconLeft: 'bank',
    label: 'navigation.menu.label.banking',
    subItems: [
      {
        id: NavigationMenuId.BankTransactions,
        label: 'navigation.menu.label.banking.bank_transactions',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.bank.getBankTransactionsRoute()),
        scopes: Scope.BankReconciliationRead,
      },
      {
        id: NavigationMenuId.NoBankAccounts,
        label: 'navigation.menu.label.banking.reconciliation',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.bank.getNoBankAccountsRoute()),
        scopes: Scope.BankReconciliationRead,
      },
      {
        id: NavigationMenuId.BankAccounts,
        label: 'navigation.menu.label.banking.bank_accounts',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.bank.getBankAccountsRoute()),
        scopes: Scope.BankReconciliationRead,
      },
      {
        id: NavigationMenuId.Reconciliation,
        label: 'navigation.menu.label.banking.reconciliation',
        to: EmberRoute.Bank,
        scopes: Scope.BankReconciliationRead,
      },
      {
        id: NavigationMenuId.CashPilot,
        label: 'navigation.menu.label.cashpilot',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.cashpilot.getMainRoute()),
        scopes: Scope.CashFlowAnalyticsRead,
        badge: <BetaBadge />,
        wrap: true,
      },
      {
        id: NavigationMenuId.GetALoan,
        label: 'navigation.menu.label.banking.get_a_loan',
        to: EmberRoute.Financing,
        scopes: Scope.LoansWrite,
      },
    ],
  },
  {
    id: NavigationMenuId.Accounting,
    iconLeft: 'briefcaseBusiness',
    label: 'navigation.menu.label.accounting',
    subItems: [
      {
        id: NavigationMenuId.LedgerEntries,
        label: 'navigation.menu.label.accounting.ledger_entries',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.transactions.getMainRoute()),
      },
      {
        id: NavigationMenuId.Daybook,
        label: 'navigation.menu.label.accounting.daybook',
        to: EmberRoute.Daybooks,
        scopes: Scope.DaybookWrite,
      },
    ],
  },
  {
    id: NavigationMenuId.Documents,
    iconLeft: 'folder',
    label: 'navigation.menu.label.documents',
    to: getOrganizationViewUrlPlaceholder(reactRoute.organization.clientDocuments.getMainRoute()),
  },
  {
    id: NavigationMenuId.Reports,
    iconLeft: 'chartPie',
    label: 'navigation.menu.label.reports',
    subItems: [
      {
        id: NavigationMenuId.Financials,
        label: 'navigation.menu.label.reports.financials',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.reports.getMainRoute()),
        scopes: Scope.ReportsRead,
      },
      {
        id: NavigationMenuId.VatDeclarations,
        label: 'navigation.menu.label.reports.vat',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.vatDeclarations.getMainRoute()),
        scopes: Scope.VatReturnRead,
      },
      {
        id: NavigationMenuId.AnnualReports,
        label: 'navigation.menu.label.reports.annual_reports',
        to: EmberRoute.AnnualReports,
      },
      {
        id: NavigationMenuId.SalesReport,
        label: 'navigation.menu.label.reports.sales_report',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.salesReport.getMainRoute()),
        scopes: Scope.ReportsInvoicing,
      },
      {
        id: NavigationMenuId.ExportData,
        label: 'navigation.menu.label.reports.export_data',
        to: getOrganizationViewUrlPlaceholder(reactRoute.organization.exports.getMainRoute()),
        scopes: Scope.ExportRead,
      },
    ],
  },
]
